import React from 'react';

import { PublicLayout } from '@/components/PublicLayout';
import { AsaphButton } from '@/components/shared/Button';
import { Loader } from '@/components/shared/Loader';
import { privacyPolicyLink, termsOfServiceLink } from '@/config/constants';
import { useFormattedTitle } from '@/hooks/useFormattedTitle';
import {
	useAnalyticsEvent,
	useFireAnalyticsPageView,
} from '@/providers/AnalyticsProvider';
import { useAuth } from '@/providers/AuthProvider';
import { useEnvironment } from '@/providers/EnvironmentProvider';
import { FormHelperText } from '@material-ui/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router';

import styles from '../Login/LoginPage.module.scss';

interface AcceptRes {
	jwt: string;
}
interface InviteRes {
	id: number;
	teamId: number;
	email: string;
	createdAt: Date;
	roles: string[];
	createdBy: number;
	accepted: boolean;
	token: string;
	displayName: string;
	team: Team;
}

interface Team {
	id: number;
	name: string;
	lastChangedDate: null;
}

const Layout: React.FC<{ title: string }> = ({ children, title }) => {
	return (
		<PublicLayout
			renderFooter={
				<div className="authentication-content__privacy-policy">
					By continuing you agree to our
					<br />
					<a
						className="authentication-content__privacy-policy__link"
						href={termsOfServiceLink}
						target="_blank"
						rel="noreferrer"
					>
						Terms of Service
					</a>
					{' and '}
					<a
						className="authentication-content__privacy-policy__link"
						href={privacyPolicyLink}
						target="_blank"
						rel="noreferrer"
					>
						Privacy Policy
					</a>
				</div>
			}
		>
			<Helmet title={title} />
			{children}
		</PublicLayout>
	);
};

const getInvite = async (url: string, token?: string) => {
	if (!token) {
		throw new Error('Token invalid');
	}
	const res = await axios.get<InviteRes>(`${url}/invite/${token}`);
	return res.data;
};

const acceptInviteFetcher = async (url: string, token?: string) => {
	if (!token) {
		throw new Error('Token invalid');
	}
	const res = await axios.post<AcceptRes>(`${url}/invite/accept`, {
		token,
	});
	return res.data;
};

const AcceptInvite = () => {
	const { token } = useParams();
	const {
		api: { url },
	} = useEnvironment();
	const { authenticate } = useAuth();
	const [title] = useFormattedTitle('Asaph | Accept Invite');

	useFireAnalyticsPageView({
		enabled: !!title,
		pageViewArgs: {
			title,
		},
	});

	const { isLoading, data, error } = useQuery<InviteRes>({
		queryKey: ['get-invite', token],
		queryFn: () => getInvite(url, token),
	});
	const {
		mutateAsync: acceptInvite,
		isLoading: acceptIsLoading,
		error: acceptError,
	} = useMutation<AcceptRes>({
		mutationFn: () => acceptInviteFetcher(url, token),
		onSuccess: ({ jwt }) => {
			authenticate(jwt);
		},
	});

	const fireInviteExpiredEvent = useAnalyticsEvent('Invite_Expired');
	const fireErrorEvent = useAnalyticsEvent('Error');

	if (isLoading) {
		return <Loader />;
	}

	if (error || !data) {
		fireInviteExpiredEvent();
		if (error) {
			fireErrorEvent({
				name: 'InviteLoadError',
				details: error,
			});
		}
		return (
			<Layout title={title}>
				<h1 className={styles.heading}>Expired Invite 😶‍🌫️</h1>
				<p className={styles.text}>
					We are sorry for the inconvenience but this invite link has
					either expired or has been accepted already.
				</p>
			</Layout>
		);
	}

	if (acceptError) {
		fireErrorEvent({
			name: 'InviteLoadError',
			details: error,
		});
	}

	return (
		<Layout title={title}>
			<h1 className={styles.heading}>Accept Invite</h1>
			<p className={styles.text}>
				You have been invited to join <strong>{data.team.name}</strong>{' '}
				with {data.email}!
			</p>
			<div className="vsb-8" />
			<AsaphButton
				onClick={() => acceptInvite()}
				disabled={acceptIsLoading}
				label="Accept"
				color="blue"
				fullWidth
			/>
			<div className="vsb-20">
				{!!acceptError && (
					<FormHelperText
						error={true}
						classes={{
							root: 'authentication-error-text',
						}}
					>
						Something went wrong with accepting this invite. Reload
						the page and try again.
					</FormHelperText>
				)}
			</div>
		</Layout>
	);
};

export default AcceptInvite;
